import React, { useCallback, useMemo } from 'react';

import { useWindowDimensions } from '@/helpers/hooks';
import NoSsr from '@mui/material/NoSsr';
import Popper from '@mui/material/Popper';
import { ScaleBand } from '@mui/x-charts-vendor/d3-scale';
import { useItemTooltip, useMouseTracker } from '@mui/x-charts/ChartsTooltip';
import { useSvgRef, useXAxis, useXScale, useYScale } from '@mui/x-charts/hooks';

import { useDashboardProvider } from '../../DasboardSidebar/DashboardProvider';
import { CustomItemTooltipContent } from './CustomItemTooltipContent';
import { generateVirtualElement, MousePosition } from './generateVirtualElement';

export function ItemTooltipTopElement({ pvData, uvData }: { pvData: number[]; uvData: number[] }) {
  const { width } = useWindowDimensions();
  const tooltipData = useItemTooltip<'bar'>();
  const mousePosition = useMouseTracker();
  const { toggleMenu } = useDashboardProvider();

  const xAxis = useXAxis();
  const xScale = useXScale();
  const yScale = useYScale();
  const svgRef = useSvgRef();

  const TOOLTIP_WIDTH = 390;
  const zoomFactor = 1;

  const getDeviceType = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';

    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return 'iOS';
    }

    return 'Unknown';
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const getAdditionalMarginByDPR = useCallback(() => {
    // returns tested DPR and Marker Width
    const deviceType = getDeviceType();
    const mediaQuery = window.matchMedia('(min-width: 768px) and (max-width: 2000px)');
    if (mediaQuery.matches) {
      return {
        dpr: deviceType === 'iOS' ? 3 : deviceType === 'Android' ? window.devicePixelRatio : 2,
        additionalMargin:
          deviceType === 'iOS' ? 110 : deviceType === 'Android' ? (window.devicePixelRatio < 2.9 ? 105 : 110) : 105,
      };
    }
    return {
      dpr: window.devicePixelRatio,
      additionalMargin: deviceType === 'Android' ? (window.devicePixelRatio < 2.9 ? 105 : 110) : 105,
    };
  }, []);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const tooltipPosition: MousePosition | null = useMemo(() => {
    if (
      !tooltipData ||
      !mousePosition ||
      !xAxis.data ||
      tooltipData.identifier.type !== 'bar' ||
      tooltipData.identifier.dataIndex === undefined ||
      tooltipData.value === null ||
      svgRef.current === null
    ) {
      return null;
    }

    const xValue = xAxis.data[tooltipData.identifier.dataIndex];
    const svgRect = svgRef.current.getBoundingClientRect();
    const svgXPosition = xScale(xValue) ?? 0;
    const barCenterOffset = (xScale as ScaleBand<any>).bandwidth() / 2;

    // Calculate the top of the stack (sum of both bar values at this index)
    const stackSum =
      (pvData?.[tooltipData.identifier.dataIndex] || 0) + (uvData?.[tooltipData.identifier.dataIndex] || 0);
    const svgYPosition = yScale(stackSum) ?? 0;

    const { additionalMargin } = getAdditionalMarginByDPR();

    if (width > 2048) {
      return {
        x: svgRect.left * zoomFactor + svgXPosition + barCenterOffset + TOOLTIP_WIDTH / 2 - 190,
        y: svgRect.top * zoomFactor + svgYPosition - 10,
        height: 0,
        pointerType: 'mouse',
      };
    }

    if (width > 1440) {
      return {
        x:
          svgRect.left * zoomFactor +
          svgXPosition +
          barCenterOffset +
          TOOLTIP_WIDTH / 2 +
          (width >= 1900 && width <= 2048 ? +20 : toggleMenu ? -33 : -10),
        y: svgRect.top * zoomFactor + svgYPosition + (width >= 1900 && width <= 2048 ? 30 : 80),
        height: 0,
        pointerType: 'mouse',
      };
    }

    if (width >= 768 && width <= 2000) {
      const zoomRatio = window.devicePixelRatio;

      let xOffset = -50;
      if (zoomRatio >= 1.1) {
        xOffset = -55;
      }

      return {
        x: svgRect.left * zoomFactor + svgXPosition + barCenterOffset + TOOLTIP_WIDTH / 2 + xOffset,
        y: svgRect.top * zoomFactor + svgYPosition + additionalMargin,
        height: 0,
        pointerType: 'mouse',
      };
    }

    return {
      ...mousePosition,
      x: svgRect.left + svgXPosition + (xScale as ScaleBand<any>).step() / 2 - 5,
      y: svgRect.top + svgYPosition - 5,
    };
  }, [tooltipData, mousePosition, xAxis.data, svgRef, width, pvData, uvData, yScale, xScale, getAdditionalMarginByDPR]);

  if (!tooltipPosition || !tooltipData) return null;

  return (
    <NoSsr>
      <Popper
        sx={{ pointerEvents: 'none', zIndex: theme => theme.zIndex.modal }}
        open
        placement="top"
        anchorEl={generateVirtualElement(tooltipPosition)}
        modifiers={[{ name: 'preventOverflow', options: { boundary: 'viewport' } }]}
      >
        <CustomItemTooltipContent {...tooltipData} />
      </Popper>
    </NoSsr>
  );
}
